import Layout from "@/components/layout/Layout";
import Button from "@/components/ui/Button";
import Spinner from "@/components/ui/Spinner";
import { JSX, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAdCategoryStore } from "./store/adCategoryStore";
import { useShallow } from "zustand/react/shallow";
import useGlobalStore from "@/store/globalStore";
import { Edit, CheckCircle } from "lucide-react";
import { parameterSettingLabel } from "./component/step-four";

function settingLabel(valueType: string, restricted?: boolean): string {
  return parameterSettingLabel(valueType, restricted) || valueType;
}

function CanvasPreview({
  standaloneData,
}: {
  standaloneData: any;
  publisherName: string;
}) {
  const canvasState = standaloneData?.canvas_state;
  const canvasMaster = canvasState?.canvas_master;

  // Canvas dimensions from canvas_master info (actual canvas size)
  const canvasWidth =
    canvasMaster?.canvas_info_aggregated?.canvasWidth ?? 390;
  const canvasHeight =
    canvasMaster?.canvas_info_aggregated?.canvasHeight ?? 844;
  const bgColor =
    canvasMaster?.canvas_info_aggregated?.backgroundColor ?? "#FFFFFF";

  // Determine the selected interface name from the canvas master
  const selectedInterfaceId =
    canvasMaster?.canvas_info_aggregated?.selectedInterface;
  const interfaceName = standaloneData?.interfaces?.find(
    (i: any) => i.id === selectedInterfaceId,
  )?.name;

  // Components render at 1:1 inside the canvas, but we use CSS transform
  // to scale the whole canvas down so it fits the container without overflow.
  const maxContainerWidth = 800;
  const maxContainerHeight = 900;
  const cssScale = Math.min(
    maxContainerWidth / canvasWidth,
    maxContainerHeight / canvasHeight,
  );
  const displayWidth = canvasWidth * cssScale;
  const displayHeight = canvasHeight * cssScale;
  // Components stay at 1:1 coordinates (scale = 1)
  const scale = 1;

  const components = canvasState?.components ?? [];

  // If no standalone data at all, show static placeholder
  if (!standaloneData) {
    return (
      <div className="bg-white rounded-3xl border-2 border-[#EA33C0] w-72 min-h-[32rem] overflow-hidden shadow-lg flex items-center justify-center">
        <div className="text-gray-400 text-sm">No canvas data available</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Outer wrapper sized to the scaled-down dimensions */}
      <div
        className="rounded-lg shadow-lg overflow-hidden"
        style={{
          width: displayWidth,
          height: displayHeight,
        }}
      >
        {/* Inner canvas at full size, CSS-scaled down */}
        <div
          className="relative origin-top-left"
          style={{
            width: canvasWidth,
            height: canvasHeight,
            backgroundColor: bgColor,
            transform: `scale(${cssScale})`,
          }}
        >
          {components.length > 0 ? (
            components.map((component: any) => {
              // Pick location for the active interface, fallback chain
              const location = interfaceName
                ? component.locations?.[interfaceName] ??
                  component.locations?.mobile ??
                  component.locations?.desktop
                : component.locations?.mobile ??
                  component.locations?.desktop;
              if (!location?.location_json) return null;

              const loc = location.location_json;
              const masterName = component.canvas_component_master_name;
              const subMasterName = component.canvas_component_sub_master_name;
              const agg = component.aggregated_json ?? {};

              return (
                <div
                  key={component.id}
                  className="absolute"
                  style={{
                    left: loc.x * scale,
                    top: loc.y * scale,
                    width: (loc.width ?? 100) * scale,
                    height: (loc.height ?? 50) * scale,
                    zIndex: loc.z_index ?? 1,
                    transform: loc.rotation
                      ? `rotate(${loc.rotation}deg)`
                      : undefined,
                  }}
                >
                  <CanvasComponentRenderer
                    masterName={masterName}
                    subMasterName={subMasterName}
                    aggregatedJson={agg}
                    scale={scale}
                  />
                </div>
              );
            })
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <div className="text-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-10 h-10 mx-auto mb-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-xs">Empty canvas</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Canvas info */}
      <div className="text-xs text-gray-400 text-center">
        {canvasWidth} x {canvasHeight}
        {canvasMaster?.status ? ` · ${canvasMaster.status}` : ""}
        {components.length > 0
          ? ` · ${components.length} component${components.length > 1 ? "s" : ""}`
          : ""}
      </div>
    </div>
  );
}

function CanvasComponentRenderer({
  masterName,
  subMasterName,
  aggregatedJson,
  scale,
}: {
  masterName: string;
  subMasterName: string;
  aggregatedJson: any;
  scale: number;
}) {
  const agg = aggregatedJson;

  // --- Text component ---
  if (masterName === "text") {
    return (
      <div
        className="w-full h-full overflow-hidden"
        style={{
          fontSize: Math.max(6, (agg.textSettings?.fontSize ?? 14) * scale),
          fontFamily: agg.textSettings?.fontFamily ?? "Arial, sans-serif",
          fontWeight: agg.textSettings?.fontWeight ?? "normal",
          fontStyle: agg.textSettings?.fontStyle ?? "normal",
          textDecoration: agg.textSettings?.textDecoration ?? "none",
          color: agg.textSettings?.color ?? "#000",
          textAlign: agg.textSettings?.textAlign ?? "left",
          lineHeight: agg.textSettings?.lineHeight ?? 1.3,
        }}
      >
        {agg.content ?? agg.label ?? ""}
      </div>
    );
  }

  // --- Image component ---
  if (masterName === "image") {
    const mediaObj = agg.media;
    const imageUrl =
      mediaObj?.[0]?.url ?? mediaObj?.["0"]?.url ?? null;

    if (imageUrl) {
      return (
        <img
          src={imageUrl}
          alt={agg.label ?? "Image"}
          className="w-full h-full object-cover rounded"
        />
      );
    }
    // Empty image placeholder
    return (
      <div className="w-full h-full border border-dashed border-gray-300 rounded bg-gray-50 flex items-center justify-center">
        <div className="text-center text-gray-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-6 h-6 mx-auto"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span className="text-[8px] mt-0.5 block">
            {agg.label ?? "Image"}
          </span>
        </div>
      </div>
    );
  }

  // --- Shape component ---
  if (masterName === "shape") {
    return (
      <div
        className="w-full h-full"
        style={{
          backgroundColor: agg.fill ?? agg.color ?? agg.backgroundColor ?? "#ccc",
          borderRadius:
            subMasterName === "circle"
              ? "50%"
              : (agg.borderRadius ?? 0),
          opacity: agg.opacity ?? 1,
          border: agg.borderColor
            ? `${agg.borderWidth ?? 1}px solid ${agg.borderColor}`
            : undefined,
        }}
      />
    );
  }

  // --- Button component ---
  if (masterName === "button") {
    return (
      <div
        className="w-full h-full flex items-center justify-center text-white rounded"
        style={{
          backgroundColor: agg.backgroundColor ?? "#1877F2",
          fontSize: Math.max(6, (agg.fontSize ?? 12) * scale),
          borderRadius: agg.borderRadius ?? 4,
        }}
      >
        {agg.label ?? agg.text ?? "Button"}
      </div>
    );
  }

  // --- Video component ---
  if (masterName === "video") {
    const mediaObj = agg.media;
    const videoUrl =
      mediaObj?.[0]?.url ?? mediaObj?.["0"]?.url ?? null;

    if (videoUrl) {
      return (
        <video
          src={videoUrl}
          className="w-full h-full object-cover rounded"
          muted
          loop
          playsInline
        />
      );
    }
    // Empty video placeholder
    return (
      <div className="w-full h-full border border-dashed border-gray-300 rounded bg-gray-800 flex items-center justify-center">
        <div className="text-center text-gray-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-6 h-6 mx-auto"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-[8px] mt-0.5 block">
            {agg.label ?? "Video"}
          </span>
        </div>
      </div>
    );
  }

  // --- Audio component ---
  if (masterName === "audio") {
    return (
      <div className="w-full h-full border border-dashed border-gray-300 rounded bg-gray-50 flex items-center justify-center">
        <span className="text-[8px] text-gray-400">
          {agg.label ?? "Audio"}
        </span>
      </div>
    );
  }

  // --- Attributes / other ---
  return (
    <div className="w-full h-full border border-dashed border-gray-200 rounded bg-gray-50 flex items-center justify-center">
      <span className="text-[8px] text-gray-400 text-center px-1">
        {agg.label ?? subMasterName ?? masterName}
      </span>
    </div>
  );
}

export default function ViewAdTypePreview(): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  // Tracks which ad type is mid-approval, so one button spins rather than all.
  const [approvingId, setApprovingId] = useState<string | null>(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const adCategoryId = searchParams.get("id");
  const publisherId = searchParams.get("publisherId");
  const adInputId = searchParams.get("adInputId");

  // Map of adInput.id -> canvas builder data (so each input has its own data)
  const [canvasDataMap, setCanvasDataMap] = useState<Record<string, any>>({});
  // Map of adInput.id -> standalone canvas data (for canvas preview)
  const [standaloneDataMap, setStandaloneDataMap] = useState<Record<string, any>>({});

  const {
    getAdCategoryDetailData,
    adCategoryDetailData,
    setEditData,
    parametersData,
    fetchParametersData,
    fetchCanvasBuilderData,
    fetchStandaloneCanvasData,
    approveAdType,
  } = useAdCategoryStore(
    useShallow((state) => ({
      getAdCategoryDetailData: state.getAdCategoryDetailData,
      adCategoryDetailData: state.adCategoryDetailData,
      setEditData: state.setEditData,
      parametersData: state.parametersData,
      fetchParametersData: state.fetchParametersData,
      fetchCanvasBuilderData: state.fetchCanvasBuilderData,
      fetchStandaloneCanvasData: state.fetchStandaloneCanvasData,
      approveAdType: state.approveAdType,
    })),
  );

  const { setError, showSuccess } = useGlobalStore(
    useShallow((state) => ({
      setError: state.setError,
      showSuccess: state.showSuccess,
    })),
  );

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        await Promise.all([
          getAdCategoryDetailData(adCategoryId),
          fetchParametersData(),
        ]);
        // After detail data loaded, fetch canvas data for the specific ad input
        const detail = useAdCategoryStore.getState().adCategoryDetailData;
        const publisherType = detail?.ad_types?.find(
          (at) => at.publisher_details?.id === publisherId,
        );
        const allInputs = publisherType?.ad_inputs ?? [];
        // Filter to the specific ad input identified by adInputId (if provided)
        const targetInputs = adInputId
          ? allInputs.filter((inp) => inp.id === adInputId)
          : allInputs;
        const newCanvasDataMap: Record<string, any> = {};
        const newStandaloneDataMap: Record<string, any> = {};
        for (const input of targetInputs) {
          if (input.id) {
            await fetchCanvasBuilderData(input.id);
            const builderData = useAdCategoryStore.getState().canvasBuilderData;
            newCanvasDataMap[input.id] = builderData;
            // Fetch standalone canvas data using ad_category_mapping_id
            if (builderData?.ad_category_mapping_id) {
              try {
                const standaloneData = await fetchStandaloneCanvasData(
                  builderData.ad_category_mapping_id
                );
                newStandaloneDataMap[input.id] = standaloneData;
              } catch {
                // Standalone data is optional - preview will show placeholder if unavailable
              }
            }
          }
        }
        setCanvasDataMap(newCanvasDataMap);
        setStandaloneDataMap(newStandaloneDataMap);
      } catch (error: any) {
        setError(error.message ?? "Something went wrong");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [adCategoryId]);

  const publisherAdType = adCategoryDetailData?.ad_types?.find(
    (at) => at.publisher_details?.id === publisherId,
  );

  // Show only the specific ad input identified by adInputId; fall back to all if not provided
  const allAdInputs = publisherAdType?.ad_inputs ?? [];
  const adInputs = adInputId
    ? allAdInputs.filter((inp) => inp.id === adInputId)
    : allAdInputs;
  const publisherName = publisherAdType?.publisher_details?.name ?? "Publisher";

  const handleEditParameters = async () => {
    if (!adCategoryId) return;
    await getAdCategoryDetailData(adCategoryId);
    const detailData = useAdCategoryStore.getState().adCategoryDetailData;
    if (detailData) setEditData(detailData);
    navigate(`/create-ad-categories?id=${adCategoryId}&step=3`);
  };

  const handleEditTemplate = () => {
    if (!adCategoryId) return;
    const detail = useAdCategoryStore.getState().adCategoryDetailData;
    const publisherType = detail?.ad_types?.find(
      (at) => at.publisher_details?.id === publisherId,
    );
    const firstInputId = publisherType?.ad_inputs?.[0]?.id;
    navigate(
      `/create-ad-categories?id=${adCategoryId}&step=5${firstInputId ? `&adInputId=${firstInputId}` : ""}`,
    );
  };

  /**
   * Approve one ad type.
   *
   * The backend promotes the ad category automatically once its last draft ad
   * type is approved, so there is no separate category action in the common
   * flow. The detail data is refetched afterwards so the badges reflect both.
   */
  const handleApprove = async (adTypeId: string) => {
    try {
      setApprovingId(adTypeId);
      const result = await approveAdType(adTypeId);

      showSuccess(
        result?.ad_category_status === "approved"
          ? "Ad type approved. The ad category is now approved and selectable."
          : "Ad type approved successfully",
      );

      if (adCategoryId) {
        await getAdCategoryDetailData(adCategoryId);
      }
    } catch (error: any) {
      setError(error.message ?? "Failed to approve ad type");
    } finally {
      setApprovingId(null);
    }
  };

  if (!adCategoryId || !publisherId) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full text-gray-500">
          Ad Type not found.
        </div>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <Spinner size={30} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div>
        {/* White header block */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <button
              onClick={() =>
                navigate(`/ad-categories/publishers?id=${adCategoryId}`)
              }
              className="text-pink-500 hover:text-pink-700 text-2xl leading-none"
              aria-label="Back"
            >
              ←
            </button>
            <span className="text-base text-gray-500">View Ad Type</span>
          </div>
          <h1 className="text-xl font-semibold">
            {adCategoryDetailData?.name ?? "Ad Category"} - {publisherName}
          </h1>
        </div>

        <hr className="border-[#EA33C0]" />

        {adInputs.length === 0 ? (
          <div className="text-gray-500 text-center py-10">
            No ad types found for this publisher.
          </div>
        ) : (
          adInputs.map((input, inputIdx) => {
            const mediaTypeKey = input.media_type?.toLowerCase();
            const paramGroups: any[] = parametersData[mediaTypeKey] ?? [];
            // Resolve canvas data specific to this ad input
            const canvasData = canvasDataMap[input.id] ?? null;

            return (
              <div key={inputIdx}>
                {/* Template preview — grey background full-width */}
                <div className="bg-[#ececec] flex items-center justify-center py-16">
                  <CanvasPreview
                    standaloneData={standaloneDataMap[input.id]}
                    publisherName={publisherName}
                  />
                </div>

                {/* Ad Specs section — data from canvasBuilderData.parameters (step 5) */}
                <div className="mt-6">
                  <div className="flex">
                    <div className="text-xl font-bold mb-4">Ad Specs</div>
                    <div className="ml-auto self-center">
                      <button
                        onClick={handleEditTemplate}
                        className="text-sm text-gray-400 hover:text-pink-500 underline"
                      >
                        Link to full specs
                      </button>
                    </div>
                  </div>
                  <hr className="border-gray-300 mb-8" />
                  {canvasData?.parameters?.length > 0 ? (
                    <div className="flex items-start gap-16 flex-wrap">
                      {canvasData.parameters.map((param: any) => {
                        const def = param.parameter_definition;
                        const val = param.value;
                        const displayVal =
                          val && typeof val === "object"
                            ? Object.values(val).filter(Boolean).join(", ") ||
                              "—"
                            : (val ?? "—");
                        return (
                          <div key={def?.key ?? param.id}>
                            <div className="text-sm font-bold mb-2">
                              {def?.label ?? "Spec"}
                            </div>
                            <div className="text-sm text-gray-700">
                              {displayVal}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">
                        No ad specs configured.
                      </span>
                      <button
                        onClick={handleEditTemplate}
                        className="text-sm text-gray-400 hover:text-pink-500 underline"
                      >
                        Link to full specs
                      </button>
                    </div>
                  )}
                </div>

                {/* Objectives section */}
                {canvasData?.ad_info_blocks?.objective?.length > 0 && (
                  <div className="mt-6">
                    <div className="text-xl font-bold mb-4">Objectives</div>
                    <hr className="border-gray-300 mb-8" />
                    {canvasData.ad_info_blocks.objective.map((block: any) =>
                      block.components?.map((comp: any) => (
                        <div key={comp.id} className="mb-6">
                          <div className="text-sm font-bold mb-2">
                            {comp.title}
                          </div>
                          {comp.input_type === "dropdown" ? (
                            <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#EA33C0] rounded-full text-sm text-gray-700 bg-white min-w-[120px]">
                              <span>
                                {comp.options?.[0]?.label ??
                                  comp.placeholder_text ??
                                  "—"}
                              </span>
                              <svg
                                className="ml-auto w-4 h-4 text-[#EA33C0]"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          ) : (
                            <div className="w-96 px-4 py-2 border border-[#EA33C0] rounded-lg text-sm text-gray-400 bg-white">
                              {comp.placeholder_text ?? "Enter valid URL"}
                            </div>
                          )}
                        </div>
                      )),
                    )}
                  </div>
                )}

                {/* Content section */}
                {canvasData?.ad_info_blocks?.content?.length > 0 && (
                  <div className="mt-6">
                    <div className="text-xl font-bold mb-4">Content</div>
                    <hr className="border-gray-300 mb-8" />
                    <div className="grid grid-cols-2 gap-x-12 gap-y-6">
                      {canvasData.ad_info_blocks.content.map((block: any) =>
                        block.components?.map((comp: any) => {
                          const isTextarea = comp.input_type === "textarea";
                          const isMedia =
                            comp.input_type === "media" ||
                            comp.input_type === "image" ||
                            comp.input_type === "video";
                          const isVideoMedia = comp.input_type === "video";
                          const charLimit =
                            comp.character_limit ?? comp.soft_limit;
                          if (isMedia) {
                            return (
                              <div key={comp.id} className="col-span-2">
                                <div className="text-sm font-bold mb-1">
                                  {comp.title}
                                </div>
                                <div className="text-sm text-gray-400 mb-3">
                                  1 {isVideoMedia ? "Video" : "Image"}
                                </div>
                                <div className="w-fit">
                                  <div className="text-sm text-gray-400 mb-1">
                                    1
                                  </div>
                                  <div className="w-40 h-32 border border-gray-300 rounded-xl bg-white flex flex-col items-center justify-center text-gray-400">
                                    <span className="text-2xl leading-none mb-1">
                                      +
                                    </span>
                                    <span className="text-xs">
                                      {isVideoMedia ? "Video" : "Image"} (30MB max)
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div key={comp.id}>
                              <div className="text-sm font-bold mb-2">
                                {comp.title}
                              </div>
                              {isTextarea ? (
                                <div className="relative">
                                  <div className="w-full h-32 px-4 py-3 border border-[#EA33C0] rounded-xl text-sm text-gray-400 bg-white">
                                    {comp.placeholder_text ??
                                      "Placeholder text...."}
                                  </div>
                                  {charLimit && (
                                    <div className="text-xs text-gray-400 text-right mt-1">
                                      0/{charLimit}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="relative">
                                  <div className="w-full px-4 py-2 border border-[#EA33C0] rounded-lg text-sm text-gray-400 bg-white">
                                    {comp.placeholder_text ??
                                      "Placeholder text...."}
                                  </div>
                                  {charLimit && (
                                    <div className="text-xs text-gray-400 text-right mt-1">
                                      0/{charLimit}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        }),
                      )}
                    </div>
                  </div>
                )}

                {/* Ad Type Parameters */}
                <div className="mt-6">
                  <div className="text-xl font-bold mb-3">
                    Ad Type Parameters
                  </div>
                  <hr className="border-gray-300 mb-4" />

                  {paramGroups.map((group) => {
                    const matchedParam = input.parameters?.find(
                      (p) => p.parameter_definition?.key === group.key,
                    );

                    const valueType =
                      matchedParam?.value_type ?? "single_publisher";
                    const isRestricted =
                      (matchedParam as any)?.visibility_metadata?.restricted ===
                      true;
                    const storedValue: Record<string, any> =
                      matchedParam?.value &&
                      typeof matchedParam.value === "object"
                        ? (matchedParam.value as Record<string, any>)
                        : {};

                    return (
                      <div className="mt-6" key={group.key}>
                        <div className="text-base font-bold mb-1">
                          {group.label}
                        </div>
                        <hr className="border-gray-300 mb-3" />
                        <div className="flex gap-12">
                          {/* Setting */}
                          <div>
                            <div className="text-xs text-gray-400 mb-1">
                              Setting
                            </div>
                            <div className="text-sm">
                              {settingLabel(valueType, isRestricted)}
                            </div>
                          </div>
                          {/* Dynamic fields */}
                          {group.fields.map((field: any) => {
                            const rawVal = storedValue[field.key];
                            const displayVal =
                              rawVal !== undefined &&
                              rawVal !== null &&
                              rawVal !== ""
                                ? String(rawVal)
                                : "#";
                            return (
                              <div key={field.key}>
                                <div className="text-xs text-gray-400 mb-1">
                                  {field.label}
                                </div>
                                <div className="text-sm">{displayVal}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {/* Custom parameters */}
                  {input.custom_parameters?.map((cp: any, cpIdx: number) => (
                    <div
                      key={cpIdx}
                      className="mt-6 bg-gray-200 rounded-lg p-4"
                    >
                      <div className="text-base font-bold mb-1">
                        Custom Parameter #{cpIdx + 1}
                      </div>
                      <hr className="border-gray-300 mb-3" />
                      <div className="flex gap-12">
                        <div>
                          <div className="text-xs text-gray-400 mb-1">
                            Setting
                          </div>
                          <div className="text-sm">
                            {settingLabel(cp.setting ?? "single_publisher")}
                          </div>
                        </div>
                        {cp.name && (
                          <div>
                            <div className="text-xs text-gray-400 mb-1">
                              {cp.type ?? "Format"}
                            </div>
                            <div className="text-sm">
                              {Array.isArray(cp.values)
                                ? cp.values.join(", ") || "—"
                                : (cp.value ?? cp.name ?? "—")}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Specs & Due Dates from parameters */}
                  {(() => {
                    const specsParam = input.parameters?.find(
                      (p) => p.parameter_definition?.key === "specs",
                    );
                    const dueDatesParam = input.parameters?.find(
                      (p) => p.parameter_definition?.key === "due_dates",
                    );

                    return (
                      <>
                        {specsParam && (
                          <div className="mt-6">
                            <div className="text-base font-bold mb-1">
                              Specs
                            </div>
                            <hr className="border-gray-300 mb-3" />
                            <div className="flex gap-12">
                              <div>
                                <div className="text-xs text-gray-400 mb-1">
                                  Setting
                                </div>
                                <div className="text-sm">
                                  {settingLabel(
                                    specsParam.value_type ?? "single_publisher",
                                  )}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-400 mb-1">
                                  Publisher Specs
                                </div>
                                <div className="text-sm">
                                  {(specsParam.value as any)?.publisher_specs ??
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {dueDatesParam && (
                          <div className="mt-6">
                            <div className="text-base font-bold mb-1">
                              Due Dates
                            </div>
                            <hr className="border-gray-300 mb-3" />
                            <div className="flex gap-12">
                              <div>
                                <div className="text-xs text-gray-400 mb-1">
                                  Setting
                                </div>
                                <div className="text-sm">
                                  {settingLabel(
                                    dueDatesParam.value_type ??
                                      "single_publisher",
                                  )}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-400 mb-1">
                                  Due to Media
                                </div>
                                <div className="text-sm">
                                  {(dueDatesParam.value as any)?.due_to_media ??
                                    "N/A"}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-400 mb-1">
                                  Due to Publisher
                                </div>
                                <div className="text-sm">
                                  {(dueDatesParam.value as any)
                                    ?.due_to_publisher ?? "N/A"}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}

                  <hr className="border-gray-300 mt-8 mb-6" />

                  {/* Bottom action buttons */}
                  <div className="flex items-center gap-3">
                    <Button type="secondary" onClick={handleEditParameters}>
                      Edit Parameters
                    </Button>
                    <Button
                      type="secondary"
                      leadingIcon={<Edit className="w-4 h-4" />}
                      onClick={handleEditTemplate}
                    >
                      Edit Template
                    </Button>
                    {/* An approved ad type has nothing left to approve, so the
                        button is replaced by its state. Only approved ad types
                        appear in the flight picker and the Assets tree. */}
                    {(input as any).status === "approved" ? (
                      <span
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-green-50 text-green-700 border border-green-200"
                        title={
                          (input as any).approved_at
                            ? `Approved ${new Date((input as any).approved_at).toLocaleString()}`
                            : undefined
                        }
                      >
                        <CheckCircle className="w-4 h-4" />
                        Approved
                      </span>
                    ) : (
                      <Button
                        type="primary"
                        leadingIcon={<CheckCircle className="w-4 h-4" />}
                        isLoading={approvingId === input.id}
                        onClick={() => handleApprove(input.id)}
                      >
                        Approve Ad Type
                      </Button>
                    )}
                  </div>
                </div>

                {inputIdx < adInputs.length - 1 && (
                  <hr className="border-gray-200 mt-4" />
                )}
              </div>
            );
          })
        )}
      </div>
    </Layout>
  );
}
